/**
 * useUtilisationData
 *
 * Derives booking-based utilisation metrics from the SchedulerContext.
 * No timesheet data — all figures come from Booking records.
 *
 * "Utilised" = confirmed / completed bookings within the period.
 * "Planned"  = all non-cancelled bookings within the period.
 * There is no billable/non-billable distinction — billing is managed
 * in the external PSA system and is not available here.
 *
 * viewBy controls the aggregation dimension:
 *   individual  → one row per resource
 *   team        → rows grouped by schedulerGroup
 *   department  → rows grouped by department
 *   location    → rows grouped by location
 */
import { useMemo } from 'react';
import {
  addDays,
  eachWeekOfInterval,
  endOfWeek,
  max as dateMax,
  min as dateMin,
  parseISO,
  startOfDay,
  format,
} from 'date-fns';
import type { Booking, Resource, AppSettings, WorkingHoursProfile } from '@/types';

// ─── Public types ────────────────────────────────────────────────────────────

export type UtilisationPeriod = 'week' | 'month' | 'quarter' | 'rolling12';
export type ViewBy = 'individual' | 'team' | 'department' | 'location';
export type UtilisationType = 'utilised' | 'planned' | 'both';
export type ResourceStatus = 'on-target' | 'below-target' | 'overloaded' | 'no-bookings';

export interface UtilisationRow {
  id: string;
  name: string;
  role: string;
  department: string;
  targetPct: number;
  utilisedPct: number;    // confirmed / completed bookings ÷ available hours
  plannedPct: number;     // all non-cancelled bookings ÷ available hours
  variancePct: number;    // utilisedPct − targetPct
  utilisedHours: number;
  plannedHours: number;
  availableHours: number;
  status: ResourceStatus;
  initials: string;
  photoUrl?: string;
  utilisedColour: string;
  periodBookings: PeriodBooking[];
  weeklyUtilisation: number[];   // last 12 weeks — utilised %
  hasBurnoutFlag: boolean;
  burnoutPct?: number;
  burnoutWeek?: string;
  /** Number of resources represented (>1 when grouped) */
  memberCount: number;
}

export interface PeriodBooking {
  id: string;
  projectName: string;
  startDate: string;
  endDate: string;
  allocationPct: number;
  status: 'confirmed' | 'planned';
}

export interface ChartDataPoint {
  name: string;
  utilised: number;
  planned: number;
  target: number;
}

export interface SummaryStats {
  avgUtilised: number;
  avgPlanned: number;
  totalUtilisedHours: number;
  resourcesAtTarget: number;
  totalResources: number;
}

export interface UtilisationData {
  rows: UtilisationRow[];
  chartData: ChartDataPoint[];
  summary: SummaryStats;
  isEmpty: boolean;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const DEFAULT_TARGET_PCT = 75;

const DEFAULT_WORKING_PROFILE: WorkingHoursProfile = {
  monday: 8, tuesday: 8, wednesday: 8, thursday: 8, friday: 8,
  saturday: 0, sunday: 0,
};

const DAY_KEYS: (keyof WorkingHoursProfile)[] = [
  'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday',
];

// Use EDS CSS tokens via var() references so these colours remain theme-aware
// when used as inline styles.  (Recharts does not use these — those are
// resolved via getComputedStyle inside UtilisationReport.tsx.)
const TEAL  = 'var(--teal-500)';
const AMBER = 'var(--warning-500)';
const RED   = 'var(--destructive)';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getInitials(name: string): string {
  const parts = String(name ?? '').trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function getEffectiveProfile(
  resource: Resource,
  globalProfile: WorkingHoursProfile,
): WorkingHoursProfile {
  if (resource.customWorkingProfile) {
    return resource.customWorkingProfile;
  }
  return globalProfile;
}

function profileHoursForDay(dayOfWeek: number, profile: WorkingHoursProfile): number {
  return profile[DAY_KEYS[dayOfWeek]] ?? 0;
}

/** Sum working hours from a profile across a date interval. */
function workingHoursInInterval(start: Date, end: Date, profile: WorkingHoursProfile): number {
  let total = 0;
  const cursor = new Date(start);
  while (cursor <= end) {
    total += profileHoursForDay(cursor.getDay(), profile);
    cursor.setDate(cursor.getDate() + 1);
  }
  return total;
}

function durationFactor(durationType: Booking['durationType']): number {
  switch (durationType) {
    case 'half-day':
    case 'half-day-am':
    case 'half-day-pm':
      return 0.5;
    default:
      return 1;
  }
}

/** Hours a booking contributes inside [rangeStart, rangeEnd] using the resource's working pattern. */
function bookingHoursInRange(
  booking: Booking,
  rangeStart: Date,
  rangeEnd: Date,
  profile: WorkingHoursProfile,
): number {
  const bStart = parseISO(booking.startTime);
  let bEnd     = parseISO(booking.endTime);

  // For day-based bookings where endTime <= startTime (e.g. single-day
  // bookings stored with the same timestamp for start and end), treat the
  // booking as spanning at least one full day.
  if (booking.durationType !== 'hourly' && bEnd.getTime() <= bStart.getTime()) {
    bEnd = addDays(startOfDay(bStart), 1);
  }

  const overlapStart = dateMax([bStart, rangeStart]);
  const overlapEnd   = dateMin([bEnd,   rangeEnd]);
  if (overlapStart >= overlapEnd) return 0;

  // Hourly bookings: derive total hours from the start/end time span,
  // then pro-rate for partial overlap with the date range.
  if (booking.durationType === 'hourly') {
    const bookingMs = bEnd.getTime() - bStart.getTime();
    if (bookingMs > 0) {
      const totalHours = bookingMs / (1000 * 60 * 60);
      const overlapMs  = overlapEnd.getTime() - overlapStart.getTime();
      return totalHours * (overlapMs / bookingMs);
    }
  }

  // Day-based bookings: subtract 1 ms so the working-day count treats the
  // end as inclusive of the last booked day rather than spilling into the
  // following day.
  const inclusiveEnd = new Date(overlapEnd.getTime() - 1);
  if (inclusiveEnd < overlapStart) return 0;
  const hours = workingHoursInInterval(overlapStart, inclusiveEnd, profile);
  return hours * durationFactor(booking.durationType);
}

function resolveColour(utilisedPct: number, targetPct: number): string {
  if (utilisedPct > 100)              return RED;
  if (utilisedPct >= targetPct)       return TEAL;
  if (utilisedPct >= targetPct - 10)  return AMBER;
  return RED;
}

function resolveStatus(
  utilisedPct: number,
  targetPct: number,
  utilisedHours: number,
  plannedHours: number,
): ResourceStatus {
  if (utilisedHours === 0 && plannedHours === 0) return 'no-bookings';
  if (utilisedPct > 100)               return 'overloaded';
  if (utilisedPct >= targetPct)        return 'on-target';
  return 'below-target';
}

/** Resolve the grouping key for a resource given the current viewBy. */
function groupKeyFor(r: Resource, viewBy: ViewBy): string {
  switch (viewBy) {
    case 'team':       return r.schedulerGroup ?? r.department ?? 'No team';
    case 'department': return r.department ?? r.schedulerGroup ?? 'No department';
    case 'location':   return r.location ?? 'No location';
    default:           return r.id; // individual — each resource is its own group
  }
}

// ─── Per-resource computation ─────────────────────────────────────────────────

interface ResourceMetrics {
  resource: Resource;
  targetPct: number;
  utilisedHours: number;
  plannedHours: number;
  availableHours: number;
  utilisedPct: number;
  plannedPct: number;
  variancePct: number;
  periodBookings: PeriodBooking[];
  weeklyUtilisation: number[];
  hasBurnoutFlag: boolean;
  burnoutPct?: number;
  burnoutWeek?: string;
}

function computeResourceMetrics(
  resource: Resource,
  resourceBookings: Booking[],
  dateFrom: Date,
  dateTo: Date,
  targetPct: number,
  last12WeekStarts: Date[],
  profile: WorkingHoursProfile,
): ResourceMetrics {
  // Available hours for this resource based on their working pattern
  const availableHoursTotal = workingHoursInInterval(dateFrom, dateTo, profile);

  // Only bookings within the selected date range, not cancelled
  const periodBkgs = resourceBookings.filter((b) => {
    if (b.status === 'cancelled') return false;
    const bStart = parseISO(b.startTime);
    const bEnd   = parseISO(b.endTime);
    return bStart < dateTo && bEnd > dateFrom;
  });

  let utilisedHours = 0;
  let plannedHours  = 0;

  const periodBookings: PeriodBooking[] = periodBkgs.flatMap((b) => {
    const hrs = bookingHoursInRange(b, dateFrom, dateTo, profile);
    const isConfirmed = b.status === 'confirmed' || b.status === 'completed';
    if (isConfirmed) utilisedHours += hrs;
    else             plannedHours  += hrs;

    // Exclude bookings that contribute zero hours to this period — they add
    // noise to the drawer without representing any utilisation impact.
    if (hrs <= 0) return [];

    const bStart = parseISO(b.startTime);
    const bEnd   = parseISO(b.endTime);
    const bookingCapacity = workingHoursInInterval(bStart, bEnd, profile);
    const allocationPct = bookingCapacity > 0
      ? Math.round((hrs / bookingCapacity) * 100)
      : 0;

    // Exclude bookings where the computed allocation is 0% — these are
    // boundary-edge bookings (e.g. 15-minute overlap) that are meaningless in
    // a utilisation context.
    if (allocationPct === 0) return [];

    return [{
      id:            b.id,
      projectName:   b.projectName,
      startDate:     format(bStart, 'dd MMM yyyy'),
      endDate:       format(bEnd,   'dd MMM yyyy'),
      allocationPct,
      status:        isConfirmed ? 'confirmed' : 'planned',
    }];
  });

  const utilisedPct = availableHoursTotal > 0
    ? Math.round((utilisedHours / availableHoursTotal) * 100)
    : 0;
  const plannedPct = availableHoursTotal > 0
    ? Math.round(((utilisedHours + plannedHours) / availableHoursTotal) * 100)
    : 0;
  const variancePct = utilisedPct - targetPct;

  // Sparkline — utilised % per week (last 12)
  const weeklyUtilisation = last12WeekStarts.map((ws) => {
    const we     = endOfWeek(ws, { weekStartsOn: 1 });
    const wAvail = workingHoursInInterval(ws, we, profile);
    if (wAvail === 0) return 0;
    let wHours = 0;
    for (const b of resourceBookings) {
      if (b.status === 'confirmed' || b.status === 'completed') {
        wHours += bookingHoursInRange(b, ws, we, profile);
      }
    }
    return Math.round((wHours / wAvail) * 100);
  });

  const recentMax      = Math.max(...weeklyUtilisation.slice(-4));
  const hasBurnoutFlag = recentMax > 100;
  const burnoutWeekIdx = weeklyUtilisation.slice(-4).findIndex((v) => v > 100);
  const burnoutWeek    = hasBurnoutFlag && burnoutWeekIdx >= 0
    ? format(last12WeekStarts[last12WeekStarts.length - 4 + burnoutWeekIdx], "'w/c' dd MMM")
    : undefined;

  return {
    resource,
    targetPct,
    utilisedHours,
    plannedHours,
    availableHours: availableHoursTotal,
    utilisedPct,
    plannedPct,
    variancePct,
    periodBookings,
    weeklyUtilisation,
    hasBurnoutFlag,
    burnoutPct: hasBurnoutFlag ? recentMax : undefined,
    burnoutWeek,
  };
}

// ─── Aggregation ──────────────────────────────────────────────────────────────

/**
 * Collapse a set of per-resource metrics into a single UtilisationRow.
 * Used for team / department / location views.
 */
function aggregateGroup(
  groupKey: string,
  metrics: ResourceMetrics[],
): UtilisationRow {
  const totalUtilised  = metrics.reduce((s, m) => s + m.utilisedHours,  0);
  const totalPlanned   = metrics.reduce((s, m) => s + m.plannedHours,   0);
  const totalAvailable = metrics.reduce((s, m) => s + m.availableHours, 0);

  const utilisedPct = totalAvailable > 0
    ? Math.round((totalUtilised / totalAvailable) * 100)
    : 0;
  const plannedPct = totalAvailable > 0
    ? Math.round(((totalUtilised + totalPlanned) / totalAvailable) * 100)
    : 0;

  // Use the average target across members
  const avgTarget = metrics.length > 0
    ? Math.round(metrics.reduce((s, m) => s + m.targetPct, 0) / metrics.length)
    : DEFAULT_TARGET_PCT;

  const variancePct = utilisedPct - avgTarget;

  // Aggregate weekly sparkline by averaging member values per week
  const weeklyUtilisation = metrics[0]?.weeklyUtilisation.map((_, wi) => {
    const vals = metrics.map((m) => m.weeklyUtilisation[wi] ?? 0);
    return Math.round(vals.reduce((s, v) => s + v, 0) / vals.length);
  }) ?? [];

  const hasBurnoutFlag = metrics.some((m) => m.hasBurnoutFlag);
  const maxBurnout     = hasBurnoutFlag
    ? Math.max(...metrics.filter((m) => m.hasBurnoutFlag).map((m) => m.burnoutPct ?? 0))
    : undefined;
  const burnoutWeek    = hasBurnoutFlag
    ? metrics.find((m) => m.hasBurnoutFlag)?.burnoutWeek
    : undefined;

  // Collect all period bookings from all members
  const allBookings = metrics.flatMap((m) => m.periodBookings);

  // Derive a sensible label: the group key itself is the name
  const firstName   = metrics[0]?.resource.name ?? groupKey;
  const displayRole = metrics.length === 1 ? metrics[0].resource.role : `${metrics.length} members`;
  const dept        = metrics[0]?.resource.schedulerGroup
    ?? metrics[0]?.resource.department
    ?? '—';

  return {
    id:                 groupKey,
    name:               groupKey,
    role:               displayRole,
    department:         dept,
    targetPct:          avgTarget,
    utilisedPct,
    plannedPct,
    variancePct,
    utilisedHours:      totalUtilised,
    plannedHours:       totalPlanned,
    availableHours:     totalAvailable,
    status:             resolveStatus(utilisedPct, avgTarget, totalUtilised, totalPlanned),
    initials:           getInitials(firstName),
    utilisedColour:     resolveColour(utilisedPct, avgTarget),
    periodBookings:     allBookings,
    weeklyUtilisation,
    hasBurnoutFlag,
    burnoutPct:         maxBurnout,
    burnoutWeek,
    memberCount:        metrics.length,
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useUtilisationData(
  dateFrom: Date,
  dateTo: Date,
  viewBy: ViewBy,
  selectedResourceIds: string[],
  selectedDepartments: string[],
  selectedTeams: string[],
  selectedRoles: string[],
  resources: Resource[],
  bookings: Booking[],
  settings: AppSettings,
): UtilisationData {

  return useMemo(() => {
    // Filter to real resources (not the system 'unallocated' entry)
    const realResources = resources.filter(
      (r) => r.id !== 'unallocated' && r.status !== 'inactive',
    );

    // Apply resource / department / team / role filters
    const filtered = realResources.filter((r) => {
      if (selectedResourceIds.length > 0 && !selectedResourceIds.includes(r.id)) return false;
      if (selectedDepartments.length > 0 && !selectedDepartments.includes(r.department ?? '')) return false;
      if (selectedTeams.length > 0 && !selectedTeams.includes(r.schedulerGroup ?? '')) return false;
      if (selectedRoles.length > 0 && !selectedRoles.includes(r.role ?? '')) return false;
      return true;
    });

    if (filtered.length === 0) {
      return {
        rows: [],
        chartData: [],
        summary: { avgUtilised: 0, avgPlanned: 0, totalUtilisedHours: 0, resourcesAtTarget: 0, totalResources: 0 },
        isEmpty: true,
      };
    }

    // Resolve global working profile from settings
    const globalProfile: WorkingHoursProfile =
      (settings as unknown as AppSettings).globalWorkingProfile ?? DEFAULT_WORKING_PROFILE;

    // Derive target % from the utilisationTiers configuration.
    // The "on-track" tier (tier-2) represents the target range — its
    // minPercentage is the threshold above which a resource is at target.
    const targetPct: number = (() => {
      const tiers = settings.utilisationTiers;
      if (tiers && tiers.length > 0) {
        const onTrackTier = tiers.find(t => String(t.id) === 'tier-2' && t.enabled);
        if (onTrackTier) return onTrackTier.minPercentage;
        const sorted = tiers.filter(t => t.enabled).sort((a, b) => a.minPercentage - b.minPercentage);
        if (sorted.length >= 2) return sorted[1].minPercentage;
      }
      return DEFAULT_TARGET_PCT;
    })();

    // Pre-bucket bookings by resourceId for performance
    const bookingsByResource: Record<string, Booking[]> = {};
    for (const b of bookings) {
      const rid = b.resourceId ?? '';
      if (!rid) continue;
      if (!bookingsByResource[rid]) bookingsByResource[rid] = [];
      bookingsByResource[rid].push(b);
    }

    // Build sparkline: last 12 ISO weeks
    const today = new Date();
    const last12WeekStarts = eachWeekOfInterval(
      {
        start: new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7 * 11),
        end:   today,
      },
      { weekStartsOn: 1 },
    ).slice(-12);

    // ── Compute per-resource metrics ──────────────────────────────────────────
    const allMetrics: ResourceMetrics[] = filtered.map((r) => {
      const profile = getEffectiveProfile(r, globalProfile);
      return computeResourceMetrics(
        r,
        bookingsByResource[r.id] ?? [],
        dateFrom,
        dateTo,
        targetPct,
        last12WeekStarts,
        profile,
      );
    });

    // ── Build rows — individual or grouped ───────────────────────────────────
    let rows: UtilisationRow[];

    if (viewBy === 'individual') {
      // One row per resource
      rows = allMetrics.map((m) => ({
        id:              m.resource.id,
        name:            m.resource.name,
        role:            m.resource.role,
        department:      m.resource.schedulerGroup ?? m.resource.department ?? '—',
        targetPct:       m.targetPct,
        utilisedPct:     m.utilisedPct,
        plannedPct:      m.plannedPct,
        variancePct:     m.variancePct,
        utilisedHours:   m.utilisedHours,
        plannedHours:    m.plannedHours,
        availableHours:  m.availableHours,
        status:          resolveStatus(m.utilisedPct, m.targetPct, m.utilisedHours, m.plannedHours),
        initials:        getInitials(m.resource.name),
        photoUrl:        m.resource.photoUrl,
        utilisedColour:  resolveColour(m.utilisedPct, m.targetPct),
        periodBookings:  m.periodBookings,
        weeklyUtilisation: m.weeklyUtilisation,
        hasBurnoutFlag:  m.hasBurnoutFlag,
        burnoutPct:      m.burnoutPct,
        burnoutWeek:     m.burnoutWeek,
        memberCount:     1,
      }));
    } else {
      // Group by the selected dimension and aggregate
      const groups = new Map<string, ResourceMetrics[]>();
      for (const m of allMetrics) {
        const key = groupKeyFor(m.resource, viewBy);
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(m);
      }
      rows = Array.from(groups.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, members]) => aggregateGroup(key, members));
    }

    // ── Chart data ────────────────────────────────────────────────────────────
    const chartData: ChartDataPoint[] = rows.map((row) => ({
      // Individual: first name; Grouped: full group name (truncated in chart)
      name:     viewBy === 'individual' ? row.name.split(' ')[0] : row.name,
      utilised: row.utilisedPct,
      planned:  row.plannedPct,
      target:   row.targetPct,
    }));

    // ── Summary stats ─────────────────────────────────────────────────────────
    const activeRows = rows.filter((r) => r.utilisedHours > 0 || r.plannedHours > 0);
    const avgUtilised = activeRows.length > 0
      ? Math.round(activeRows.reduce((s, r) => s + r.utilisedPct, 0) / activeRows.length)
      : 0;
    const avgPlanned = activeRows.length > 0
      ? Math.round(activeRows.reduce((s, r) => s + r.plannedPct, 0) / activeRows.length)
      : 0;
    const totalUtilisedHours  = rows.reduce((s, r) => s + r.utilisedHours,  0);
    const resourcesAtTarget   = rows.filter(
      (r) => r.utilisedPct >= r.targetPct && (r.utilisedHours > 0 || r.plannedHours > 0),
    ).length;

    const isEmpty =
      rows.length === 0 ||
      rows.every((r) => r.utilisedHours === 0 && r.plannedHours === 0);

    return {
      rows,
      chartData,
      summary: {
        avgUtilised,
        avgPlanned,
        totalUtilisedHours,
        resourcesAtTarget,
        totalResources: rows.length,
      },
      isEmpty,
    };
  }, [resources, bookings, settings, dateFrom, dateTo, viewBy, selectedResourceIds, selectedDepartments, selectedTeams, selectedRoles]);
}
